import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import ReservedStockIndicator from './ReservedStockIndicator';
import LeftoverIndicator from './LeftoverIndicator';

// Mobile-optimized inventory item card
const InventoryMobileCard = React.memo(({
  item,
  onItemClick,
  selectedClientId = 'global',
  viewMode = 'finished'
}) => {
  const stockLevel = React.useMemo(() => {
    if (!selectedClientId || selectedClientId === 'global') {
      return item.remaining_quantity || 0;
    }
    // NEW: Flattened API returns client_quantity directly (location-based records)
    if (typeof item.client_quantity !== 'undefined' && item.client_quantity !== null) {
      return item.client_quantity || 0;
    }
    // OLD: Fallback for nested client inventory (backward compatibility)
    if (item.client_inventory?.[selectedClientId]) {
      return item.client_inventory[selectedClientId].remaining_quantity || 0;
    }
    return 0;
  }, [item, selectedClientId]);
  const isLowStock = stockLevel <= 10;
  const isOutOfStock = stockLevel === 0;

  const handleClick = React.useCallback(() => {
    if (onItemClick && item._id) {
      onItemClick(item._id);
    }
  }, [onItemClick, item._id]);

  const handleKeyDown = React.useCallback((e) => {
    if ((e.key === 'Enter' || e.key === ' ') && onItemClick && item._id) {
      e.preventDefault();
      onItemClick(item._id);
    }
  }, [onItemClick, item._id]);

  return (
    <Card
      className="mb-4 cursor-pointer hover:shadow-md hover:-translate-y-px transition-all focus:outline-2 focus:outline-primary focus:outline-offset-2"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${item.name}`}
    >
      <CardContent className="pb-4 pt-4">
        {/* Header with name and status */}
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold flex-grow pr-2 leading-tight">
            {item.name}
          </h3>
          <Badge
            variant={isOutOfStock ? 'destructive' : isLowStock ? 'warning' : 'default'}
            className={`flex-shrink-0 ${
              isOutOfStock ? 'bg-destructive/20 text-destructive' :
              isLowStock ? 'bg-warning/20 text-warning' :
              'bg-success/20 text-success'
            }`}
          >
            {isOutOfStock ? 'Out of Stock' : isLowStock ? 'Low Stock' : 'In Stock'}
          </Badge>
        </div>

        {/* Product ID */}
        <p className="text-sm text-primary font-medium mb-2 underline">
          ID: {item.product_id || item.product_code}
        </p>

        <hr className="my-3 border-border" />

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Left Column */}
          <div>
            <span className="text-xs text-muted-foreground block">Category</span>
            <Badge variant="outline" className="mt-1 text-xs max-w-full truncate">
              {item.category || 'N/A'}
            </Badge>
          </div>

          {/* Right Column */}
          <div>
            <span className="text-xs text-muted-foreground block">Location</span>
            <p className="text-sm mt-1">
              {selectedClientId && selectedClientId !== 'global'
                ? (item.location || item.client_inventory?.[selectedClientId]?.location || 'Unassigned')
                : (item.location || 'Unassigned')}
            </p>
          </div>

          {/* Client Name (global view only) */}
          {(!selectedClientId || selectedClientId === 'global') && (
            <div className="col-span-2">
              <span className="text-xs text-muted-foreground block">客户</span>
              <p className="text-sm font-medium mt-1">
                {item.client_name || '-'}
              </p>
            </div>
          )}
        </div>

        {/* Stock Level Row */}
        <div className="mt-3">
          <span className="text-xs text-muted-foreground block mb-1">Stock Level</span>
          <div className="flex items-center gap-2">
            <span
              className={`text-xl font-bold tabular-nums ${
                isOutOfStock ? 'text-destructive' :
                isLowStock ? 'text-warning' :
                'text-success'
              }`}
            >
              {stockLevel}
            </span>
            <span className="text-sm text-muted-foreground">units</span>
            {isLowStock && !isOutOfStock && (
              <Badge variant="outline" className="ml-auto bg-warning/20 text-warning">
                Low
              </Badge>
            )}
            {isOutOfStock && (
              <Badge variant="destructive" className="ml-auto">
                Out
              </Badge>
            )}
          </div>
          {/* Reserved Stock Indicator */}
          {item.reservation_info?.total_reserved > 0 && (
            <div className="mt-2">
              <ReservedStockIndicator
                reservedQuantity={item.reservation_info.total_reserved}
                reservationDetails={item.reservation_info.details || []}
                size="small"
              />
            </div>
          )}

          {/* Purchase Pending Indicator (raw material only) */}
          {viewMode === 'raw_material' && (
            <div className="mt-2">
              <span className="text-xs text-muted-foreground block mb-1">采购未收</span>
              <span className={`text-sm font-medium ${item.purchase_pending_quantity > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                {item.purchase_pending_quantity > 0 ? `${item.purchase_pending_quantity} 件` : '-'}
              </span>
              {item.purchase_pending_quantity > 0 && item.purchase_pending_po_ids?.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {item.purchase_pending_po_ids.map((poId) => (
                    <span
                      key={poId}
                      className="text-[10px] font-mono bg-muted px-1 py-0.5 rounded text-muted-foreground"
                    >
                      {poId}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Leftovers Indicator */}
          {item.leftover_count > 0 && (
            <div className="mt-2">
              <LeftoverIndicator
                productId={item.product_id}
                leftoverCount={item.leftover_count}
                leftoverTotalQuantity={item.leftover_total_quantity}
              />
            </div>
          )}
        </div>

        {/* Part Number (if available) */}
        {item.part_number && (
          <div className="mt-2">
            <span className="text-xs text-muted-foreground">
              Part Number: <span>{item.part_number}</span>
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

InventoryMobileCard.displayName = 'InventoryMobileCard';

export default InventoryMobileCard;
